import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common components
import {
  Button,
  TextInput,
  DynamicDropdown,
  StaticDropdown,
  FullScreenLoading,
} from "src/Components";
// Requests
import {
  editExecusionTask,
  addExecusionTask,
  getTaskOptionInstrument,
  getTaskAccounts,
} from "src/Requests/automaticMarketMaking";
// Utilities
import * as R from "ramda";
import { packetErrorHandler } from "src/Utils/requestUtils";
// Hooks
import { useAppSelector } from "src/App/hooks";
// Types
import {
  IExecusionSearchParams,
  IVType,
  OrderType,
} from "src/Interfaces/automaticMarketMaking";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";

interface Props {
  onClose: () => void;
}

const SettingConfig: React.FC<Props> = (props) => {
  // local store
  const [loading, setLoading] = React.useState(false);
  const [reFetchData, setRefetchData] = React.useState(false);

  const [searchParam, setSearchParam] = React.useState(
    {} as IExecusionSearchParams
  );

  const isEdit = useAppSelector((store) => store.automaticMarketMaking.isEdit);
  const taskRow = useAppSelector((store) => store.automaticMarketMaking.taskRow);
  const editTask = useAppSelector(
    (store) => store.automaticMarketMaking.editTask
  );
  React.useEffect(() => {
    if (R.isNotEmpty(editTask) && isEdit) {
      setSearchParam(editTask);
      setRefetchData(true);
    }
  }, [editTask]);

  const ivTypeOptions: DropdownItem[] = [
    { id: IVType.Manual, name: "دستی" },
    { id: IVType.Auto, name: "اتوماتیک" },
    { id: IVType.Continues, name: "پیوسته" },
  ];

  const orderTypeOptions: DropdownItem[] = [
    { id: OrderType.Open, name: "Open" },
    { id: OrderType.Offset, name: "Offset" },
  ];

  const changeHandler = (name: string, value: any) => {
    const search: Partial<IExecusionSearchParams> = { ...searchParam };
    if (name === "ivtype" && value === IVType.Auto) {
      delete search.ivManualValue;
    }
    setSearchParam({ ...search, [name]: value } as IExecusionSearchParams);
  };

  const getProperFunction = () => {
    if (isEdit)
      return editExecusionTask({
        ...searchParam,
        ivManualValue:
          searchParam?.ivManualValue?.toString() === ""
            ? null
            : searchParam.ivManualValue,
      });
    return addExecusionTask({
      ...searchParam,
      ivManualValue:
        searchParam?.ivManualValue?.toString() === ""
          ? null
          : searchParam.ivManualValue,
    });
  };

  const clickHandler = () => {
    if (searchParam?.ivtype === IVType.Manual && !searchParam?.ivManualValue) {
      toast.error("لطفا iv دستی را وارد کنید.");
      return;
    }
    if (
      searchParam?.minimumTriggerIntervalSeconds == null ||
      searchParam.minimumTriggerIntervalSeconds < 0
    ) {
      toast.error("حداقل فاصله بین تریگرها باید صفر یا بیشتر باشد.");
      return;
    }
    setLoading(true);
    getProperFunction()
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          props.onClose();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <div className="w-full flex flex-col justify-center items-center gap-3">
      {isEdit && <h3 className="w-full text-base font-bold text-gray-700 pb-2 border-b border-gray-200">ویرایش تسک {editTask?.id ?? taskRow?.id}</h3>}
      {loading ? <FullScreenLoading /> : <></>}
      <div className="flex w-full items-center">
        <span className="text-sm text-gray-500 w-64">نماد</span>
        {isEdit ? (
          // Fixed for an existing task; shown from the clicked row so it does not depend on
          // the dropdown lists (which only contain currently configured base/hedger pairs).
          <TextInput
            label="نماد"
            name="insName"
            value={taskRow?.optionName ?? ""}
            onChange={() => undefined}
            disabled
          />
        ) : (
          <DynamicDropdown
            dropdown={{
              name: "insId",
              placeholder: "نماد",
            }}
            getData={getTaskOptionInstrument}
            onChange={changeHandler}
            value={searchParam?.insId}
          />
        )}
      </div>
      <div className="flex w-full items-center">
        <span className="text-sm text-gray-500 w-64">نام اکانت</span>
        {isEdit ? (
          <TextInput
            label="نام اکانت"
            name="accountName"
            value={taskRow?.username ?? ""}
            onChange={() => undefined}
            disabled
          />
        ) : (
          <DynamicDropdown
            // Remount when the instrument becomes known: the first fetch (made while
            // insId is still empty) could otherwise answer last and blank the list.
            key={searchParam?.insId ?? "no-instrument"}
            dropdown={{
              name: "accountId",
              placeholder: "اکانت",
              disabled: !searchParam?.insId,
            }}
            getData={() =>
              searchParam?.insId
                ? getTaskAccounts(searchParam.insId)
                : Promise.resolve([])
            }
            onChange={changeHandler}
            inputId={searchParam?.insId}
            value={searchParam?.accountId}
            reFetchData={reFetchData}
            setRefetchData={setRefetchData}
          />
        )}
      </div>
      <div className="flex w-full items-center">
        <span className="text-sm text-gray-500 w-64">
          حداقل فاصله دو تریگر (ثانیه)
        </span>
        <TextInput
          label="حداقل فاصله دو تریگر (ثانیه)"
          name="minimumTriggerIntervalSeconds"
          value={searchParam?.minimumTriggerIntervalSeconds?.toString()}
          onChange={changeHandler}
          kind="number"
        />
      </div>
      <div className="flex w-full items-center">
        <span className="text-sm text-gray-500 w-64">٪ تلورانس قیمت پایه</span>
        <TextInput
          label="٪ تلورانس قیمت پایه"
          name="basePriceTolerance"
          value={searchParam?.basePriceTolerance?.toString()}
          onChange={changeHandler}
        />
      </div>
      <div className="flex w-full items-center">
        <span className="text-sm text-gray-500 w-64">ضریب تعدیل قیمت پایه</span>
        <TextInput
          label="ضریب تعدیل قیمت پایه"
          name="basePriceAdjustmentFactor"
          value={searchParam?.basePriceAdjustmentFactor?.toString()}
          onChange={changeHandler}
        />
      </div>
      <div className="flex w-full items-center justify-start">
        <span className="text-sm text-gray-500 w-64">تعیین IV</span>
        <StaticDropdown
          dropdown={{
            name: "ivtype",
            placeholder: "تعیین IV",
          }}
          options={ivTypeOptions}
          onChange={changeHandler}
          value={searchParam?.ivtype}
        />
      </div>
      <div className="flex w-full items-center">
        <span className="text-sm text-gray-500 w-64">
          ضریب اختصاصی IV پیوسته
        </span>
        <TextInput
          label="ضریب اختصاصی IV پیوسته"
          name="continuousIV"
          value={searchParam?.continuousIV?.toString()}
          onChange={changeHandler}
        />
      </div>
      <div className="flex w-full items-center">
        <span className="text-sm text-gray-500 w-64">IV دستی</span>
        <TextInput
          label="IV دستی"
          name="ivManualValue"
          value={searchParam?.ivManualValue?.toString()}
          onChange={changeHandler}
        />
      </div>
      <div className="flex w-full items-center justify-start">
        <span className="text-sm text-gray-500 w-64">نوع سفارش</span>
        <StaticDropdown
          dropdown={{
            name: "orderType",
            placeholder: "نوع سفارش",
          }}
          options={orderTypeOptions}
          onChange={changeHandler}
          value={searchParam?.orderType}
        />
      </div>
      <div className="w-[200px] mt-7">
        <Button onClick={clickHandler} text="ثبت" variant="contained" />
      </div>
    </div>
  );
};

export default SettingConfig;
